/**
 * Settings View — adviser-only configuration panel.
 *
 * Two-pane layout: a left sidebar with five sections (Bonus, SLA,
 * Notifications, Access, Fee Calculator), each addressable by URL
 * (`/settings/<section>`). The Fee Calculator section is wired to
 * its own Supabase row (`fee_calculator_settings`) via
 * useFeeCalculatorSettings — it has an internal save button and is
 * not affected by the global "Save Settings" button at the bottom.
 *
 * Allows Marinus to update:
 * - PAYE equivalent amount for Wouter's bonus
 * - Notification email addresses
 * - SLA threshold
 * - Bonus milestones and amounts
 * - Authorised user accounts (whitelist)
 * - Weekly report schedule toggle
 * - Fee Calculator inputs (hourly rate, threshold, cap, VAT)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings as SettingsIcon, Save, CheckCircle2, Coins,
  Clock, Mail, Shield, Bell, Trash2, Plus, User, Calculator,
} from 'lucide-react';
import { getSettings, saveSettings } from '../api/settingsApi';
import { useFeeCalculatorSettings } from '../hooks/useFeeCalculatorSettings';
import FeeCalculatorSettingsPanel from '../components/tools/feeCalculator/SettingsPanel';

// Default settings
const DEFAULT_SETTINGS = {
  payeAmount: 0,
  marinusEmail: process.env.REACT_APP_MARINUS_EMAIL || '',
  wouterEmail: process.env.REACT_APP_WOUTER_EMAIL || '',
  slaThresholdDays: 3,
  bronzeTarget: 20,
  silverTarget: 25,
  goldTarget: 30,
  bronzeAmount: 1000,
  silverAmount: 0,
  goldAmount: 0,
  weeklyReportsEnabled: true,
  mondayReportTime: '07:00',
  fridayReportTime: '16:00',
  authorisedUsers: (process.env.REACT_APP_ALLOWED_USERS || '').split(',').filter(Boolean),
};

const SECTIONS = [
  { id: 'bonus', label: 'Bonus', icon: Coins },
  { id: 'sla', label: 'SLA', icon: Clock },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'access', label: 'Access', icon: Shield },
  { id: 'fee-calculator', label: 'Fee Calculator', icon: Calculator },
];

const VALID_IDS = SECTIONS.map((s) => s.id);
const DEFAULT_SECTION = 'bonus';

export default function Settings() {
  const { section } = useParams();
  const navigate = useNavigate();
  const activeSection = VALID_IDS.includes(section) ? section : DEFAULT_SECTION;

  // Redirect bare `/settings` (and any unknown sub-section) to the default.
  useEffect(() => {
    if (section !== activeSection) {
      navigate(`/settings/${activeSection}`, { replace: true });
    }
  }, [section, activeSection, navigate]);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');

  // Fee Calculator settings — separate Supabase row, separate save flow.
  const {
    settings: feeSettings,
    loaded: feeSettingsLoaded,
    saveSettings: saveFeeSettings,
  } = useFeeCalculatorSettings();

  // Load main settings from Supabase on mount
  const loadSettings = useCallback(async () => {
    try {
      const data = await getSettings();
      setSettings((prev) => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      const updated = await saveSettings(settings);
      setSettings((prev) => ({ ...prev, ...updated }));
      // Keep localStorage as fallback for components that still read it
      localStorage.setItem('inbox_settings', JSON.stringify(settings));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setSaveError(err.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addAuthorisedUser = () => {
    if (!newUserEmail.trim() || !newUserEmail.includes('@')) return;
    const users = [...(settings.authorisedUsers || []), newUserEmail.trim().toLowerCase()];
    updateSetting('authorisedUsers', [...new Set(users)]);
    setNewUserEmail('');
  };

  const removeAuthorisedUser = (email) => {
    updateSetting(
      'authorisedUsers',
      (settings.authorisedUsers || []).filter((u) => u !== email)
    );
  };

  // The Fee Calculator section has its own internal save button — hide
  // the global save bar to avoid confusion about which one applies.
  const showGlobalSave = activeSection !== 'fee-calculator';

  return (
    <div>
      <h2 style={styles.title}>
        <SettingsIcon size={20} /> Settings
      </h2>

      <div style={styles.layout}>
        {/* Left sidebar */}
        <nav className="settings-sidebar" style={styles.sidebar} aria-label="Settings sections">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = activeSection === s.id;
            return (
              <button
                key={s.id}
                style={{
                  ...styles.sidebarItem,
                  ...(isActive ? styles.sidebarItemActive : {}),
                }}
                onClick={() => navigate(`/settings/${s.id}`)}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={16} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Right pane */}
        <motion.div
          className="card"
          style={styles.card}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AnimatePresence mode="wait">
            {/* Bonus Section */}
            {activeSection === 'bonus' && (
              <motion.div
                key="bonus"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <h3 style={styles.sectionTitle}>
                  <Coins size={16} /> Bonus Configuration
                </h3>

                <h4 style={styles.subSectionTitle}>Milestone Targets</h4>

                <div style={styles.milestonesGrid}>
                  <div style={styles.milestoneCard}>
                    <span style={styles.milestoneEmoji}>🥉</span>
                    <div style={styles.milestoneInfo}>
                      <span style={styles.milestoneLabel}>Bronze</span>
                      <div style={styles.milestoneRow}>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Files</label>
                          <input
                            type="number"
                            value={settings.bronzeTarget}
                            onChange={(e) => updateSetting('bronzeTarget', parseInt(e.target.value) || 20)}
                            min={1}
                            style={styles.miniInput}
                          />
                        </div>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Amount (R)</label>
                          <input
                            type="number"
                            value={settings.bronzeAmount}
                            onChange={(e) => updateSetting('bronzeAmount', parseInt(e.target.value) || 1000)}
                            min={0}
                            style={styles.miniInput}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={styles.milestoneCard}>
                    <span style={styles.milestoneEmoji}>🥈</span>
                    <div style={styles.milestoneInfo}>
                      <span style={styles.milestoneLabel}>Silver</span>
                      <div style={styles.milestoneRow}>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Files</label>
                          <input
                            type="number"
                            value={settings.silverTarget}
                            onChange={(e) => updateSetting('silverTarget', parseInt(e.target.value) || 25)}
                            min={1}
                            style={styles.miniInput}
                          />
                        </div>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Amount (R)</label>
                          <input
                            type="number"
                            value={settings.silverAmount}
                            onChange={(e) => updateSetting('silverAmount', parseInt(e.target.value) || 0)}
                            min={0}
                            style={styles.miniInput}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p style={styles.milestoneHint}>
                    This bonus equals the Bronze bonus amount plus the PAYE amount applicable to the current salary payable.
                  </p>

                  <div style={styles.milestoneCard}>
                    <span style={styles.milestoneEmoji}>🥇</span>
                    <div style={styles.milestoneInfo}>
                      <span style={styles.milestoneLabel}>Gold</span>
                      <div style={styles.milestoneRow}>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Files</label>
                          <input
                            type="number"
                            value={settings.goldTarget}
                            onChange={(e) => updateSetting('goldTarget', parseInt(e.target.value) || 30)}
                            min={1}
                            style={styles.miniInput}
                          />
                        </div>
                        <div style={styles.milestoneField}>
                          <label style={styles.miniLabel}>Amount (R)</label>
                          <input
                            type="number"
                            value={settings.goldAmount}
                            onChange={(e) => updateSetting('goldAmount', parseInt(e.target.value) || 0)}
                            min={0}
                            style={styles.miniInput}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p style={styles.milestoneHint}>
                    This bonus equals the Silver bonus amount multiplied by two.
                  </p>
                </div>
              </motion.div>
            )}

            {/* SLA Section */}
            {activeSection === 'sla' && (
              <motion.div
                key="sla"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <h3 style={styles.sectionTitle}>
                  <Clock size={16} /> SLA Configuration
                </h3>

                <div style={styles.field}>
                  <label style={styles.label}>SLA Threshold (active days)</label>
                  <input
                    type="number"
                    value={settings.slaThresholdDays}
                    onChange={(e) => updateSetting('slaThresholdDays', parseInt(e.target.value) || 3)}
                    min={1}
                    max={30}
                  />
                  <p style={styles.hint}>
                    Files must reach "Awaiting Signature" within this many active days to qualify for Wouter's bonus.
                    Paused time is excluded from the count.
                  </p>
                </div>

                <div style={styles.infoBox}>
                  <h4 style={styles.infoTitle}>How the SLA clock works</h4>
                  <ul style={styles.infoList}>
                    <li>Clock starts when a file is drawn (status: In Progress)</li>
                    <li>Clock pauses when file is paused (e.g. waiting for client)</li>
                    <li>Clock resumes when file is resumed</li>
                    <li>File must reach "Awaiting Signature" within {settings.slaThresholdDays} active days</li>
                    <li>Warning emails sent when threshold is exceeded</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <motion.div
                key="notifications"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <h3 style={styles.sectionTitle}>
                  <Mail size={16} /> Notification Settings
                </h3>

                <div style={styles.field}>
                  <label style={styles.label}>
                    <User size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                    Marinus's email
                  </label>
                  <input
                    type="email"
                    value={settings.marinusEmail}
                    onChange={(e) => updateSetting('marinusEmail', e.target.value)}
                    placeholder="marinus@optimum.co.za"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>
                    <User size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                    Wouter's email
                  </label>
                  <input
                    type="email"
                    value={settings.wouterEmail}
                    onChange={(e) => updateSetting('wouterEmail', e.target.value)}
                    placeholder="wouter@optimum.co.za"
                  />
                </div>

                <div style={styles.divider} />

                <h4 style={styles.subSectionTitle}>Weekly Reports</h4>

                <div style={styles.field}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={settings.weeklyReportsEnabled}
                      onChange={(e) => updateSetting('weeklyReportsEnabled', e.target.checked)}
                      style={{ width: 'auto', marginRight: '0.5rem' }}
                    />
                    <span>Send weekly progress reports</span>
                  </label>
                </div>

                {settings.weeklyReportsEnabled && (
                  <div style={styles.reportSchedule}>
                    <div style={styles.scheduleItem}>
                      <span style={styles.scheduleDay}>Monday</span>
                      <span style={styles.scheduleTime}>07:00</span>
                      <span style={styles.scheduleDesc}>Pipeline overview & weekly kickoff</span>
                    </div>
                    <div style={styles.scheduleItem}>
                      <span style={styles.scheduleDay}>Friday</span>
                      <span style={styles.scheduleTime}>15:00</span>
                      <span style={styles.scheduleDesc}>Week-in-review & progress stats</span>
                    </div>
                  </div>
                )}

                <div style={styles.infoBox}>
                  <h4 style={styles.infoTitle}>Automatic notifications</h4>
                  <ul style={styles.infoList}>
                    <li>New file submitted — Wouter is notified immediately</li>
                    <li>SLA breach — both users receive a warning email</li>
                    <li>Calendar trigger — email prompt after consultations</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Access Section */}
            {activeSection === 'access' && (
              <motion.div
                key="access"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <h3 style={styles.sectionTitle}>
                  <Shield size={16} /> Authorised Users
                </h3>

                <p style={styles.accessDesc}>
                  Only these Google accounts can access Inbox. Unauthorised users
                  will see an access-denied screen after login.
                </p>

                <div style={styles.usersList}>
                  {(settings.authorisedUsers || []).map((email) => (
                    <div key={email} style={styles.userItem}>
                      <div style={styles.userAvatar}>
                        {email.charAt(0).toUpperCase()}
                      </div>
                      <span style={styles.userEmail}>{email}</span>
                      <span style={styles.userRole}>
                        {email.toLowerCase() === (process.env.REACT_APP_ADVISER_EMAIL || '').toLowerCase()
                          ? 'Adviser'
                          : email.toLowerCase() === (process.env.REACT_APP_ASSISTANT_EMAIL || '').toLowerCase()
                            ? 'Administration Assistant'
                            : '—'}
                      </span>
                      <button
                        style={styles.userRemoveBtn}
                        onClick={() => removeAuthorisedUser(email)}
                        title="Remove user"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={styles.addUserRow}>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="email@optimum.co.za"
                    onKeyDown={(e) => e.key === 'Enter' && addAuthorisedUser()}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={addAuthorisedUser}
                    disabled={!newUserEmail.includes('@')}
                    style={{ flexShrink: 0 }}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>

                <p style={styles.hint}>
                  Note: Role assignment (Adviser/Assistant) is configured via environment variables.
                  Adding a user here only grants login access.
                </p>
              </motion.div>
            )}

            {/* Fee Calculator Section */}
            {activeSection === 'fee-calculator' && (
              <motion.div
                key="fee-calculator"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {feeSettingsLoaded ? (
                  <FeeCalculatorSettingsPanel
                    settings={feeSettings}
                    onSave={saveFeeSettings}
                  />
                ) : (
                  <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem' }}>Loading…</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Save Button — hidden on Fee Calculator (it has its own save flow) */}
          {showGlobalSave && (
            <div style={styles.saveSection}>
              <motion.button
                className="btn btn-primary"
                style={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {saved ? (
                  <>
                    <CheckCircle2 size={16} /> Saved
                  </>
                ) : saving ? (
                  <>
                    <Save size={16} /> Saving…
                  </>
                ) : (
                  <>
                    <Save size={16} /> Save Settings
                  </>
                )}
              </motion.button>
              {saveError && (
                <p style={styles.saveError}>{saveError}</p>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const styles = {
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1.3rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: 'var(--cp-text)',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '200px minmax(0, 1fr)',
    gap: '1.5rem',
    alignItems: 'start',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    position: 'sticky',
    top: '5rem',
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.6rem 0.85rem',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--cp-text-muted)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
  },
  sidebarItemActive: {
    background: 'rgba(15, 76, 117, 0.08)',
    color: 'var(--cp-primary)',
    borderColor: 'rgba(15, 76, 117, 0.16)',
    fontWeight: 600,
  },
  card: {
    maxWidth: '760px',
    padding: '2rem',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
    marginBottom: '1rem',
  },
  subSectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.6rem',
    marginTop: '0.5rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.3rem',
  },
  hint: {
    fontSize: '0.73rem',
    color: 'var(--cp-text-muted)',
    marginTop: '0.3rem',
    lineHeight: 1.4,
  },
  milestonesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  milestoneCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    background: '#FAFCFE',
    borderRadius: '10px',
    border: '1px solid var(--cp-surface-alt)',
  },
  milestoneEmoji: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  milestoneInfo: {
    flex: 1,
  },
  milestoneLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    display: 'block',
    marginBottom: '0.3rem',
  },
  milestoneRow: {
    display: 'flex',
    gap: '0.6rem',
  },
  milestoneField: {
    flex: 1,
  },
  miniLabel: {
    display: 'block',
    fontSize: '0.65rem',
    fontWeight: 500,
    color: 'var(--cp-text-muted)',
    marginBottom: '0.15rem',
  },
  miniInput: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.85rem',
  },
  milestoneHint: {
    fontSize: '0.73rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.4,
    marginTop: '0.25rem',
    paddingLeft: '2.75rem',
  },
  divider: {
    borderTop: '1px solid var(--cp-surface-alt)',
    margin: '1rem 0',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    cursor: 'pointer',
  },
  reportSchedule: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    marginBottom: '1rem',
    marginTop: '0.5rem',
  },
  scheduleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--cp-bg)',
    borderRadius: '8px',
  },
  scheduleDay: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
    minWidth: '65px',
  },
  scheduleTime: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'var(--cp-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  scheduleDesc: {
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    flex: 1,
  },
  infoBox: {
    background: 'rgba(27, 154, 170, 0.06)',
    border: '1px solid rgba(27, 154, 170, 0.12)',
    borderRadius: '10px',
    padding: '0.85rem',
    marginTop: '0.75rem',
  },
  infoTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
    marginBottom: '0.4rem',
  },
  infoList: {
    paddingLeft: '1rem',
    margin: 0,
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.7,
  },
  accessDesc: {
    fontSize: '0.85rem',
    color: 'var(--cp-text-muted)',
    marginBottom: '1rem',
    lineHeight: 1.5,
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    marginBottom: '0.75rem',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.5rem 0.65rem',
    background: '#FAFCFE',
    borderRadius: '8px',
    border: '1px solid var(--cp-surface-alt)',
  },
  userAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--cp-primary), var(--cp-secondary))',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  userEmail: {
    flex: 1,
    fontSize: '0.8rem',
    color: 'var(--cp-text)',
    fontWeight: 500,
  },
  userRole: {
    fontSize: '0.7rem',
    color: 'var(--cp-text-muted)',
    background: 'var(--cp-bg)',
    padding: '0.15rem 0.4rem',
    borderRadius: '4px',
  },
  userRemoveBtn: {
    padding: '4px',
    background: 'none',
    border: 'none',
    color: 'var(--cp-danger)',
    cursor: 'pointer',
    opacity: 0.5,
    transition: 'opacity 0.15s',
    display: 'flex',
  },
  addUserRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  saveSection: {
    borderTop: '1px solid var(--cp-surface-alt)',
    paddingTop: '1.25rem',
    marginTop: '1.5rem',
  },
  saveBtn: {
    width: '100%',
    padding: '0.85rem',
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  saveError: {
    marginTop: '0.75rem',
    padding: '0.6rem 0.85rem',
    background: 'rgba(192, 57, 43, 0.08)',
    border: '1px solid rgba(192, 57, 43, 0.2)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: 'var(--cp-danger)',
    lineHeight: 1.4,
  },
};
